import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';

import { FeatureFlag } from 'constants/featureFlags';
import useLoanId from 'hooks/useLoanId';

import { openDialogAction } from 'store/global/actions';
import {
  selectFormattedLoanFeesView,
  selectIsLoanCicFeeReasonExist,
  selectIsRefinanceFeesFlow,
  selectTaxesInsuranceEscrowedLoanFeesView,
} from 'store/loanApplications/selectors';

import LoanFeeCicForm from '../components/LoanFeeCicForm';
import { LoanFeesTestIds } from '../LoanFees.testIds';
import LodeStarQuestions from '../LodeStarQuestions';

import { getLeSectionOptions } from './LoanFeesBody.constants';
import {
  dividerStyles,
  summariesOfTransactionsStyles,
  titleTypographyStyles,
  wrapperHeaderStyles,
  wrapperStyles,
} from './LoanFeesBody.styles';
import LoanFeeSection from './LoanFeeSection';

const LoanFeesBody = loanFeesBodyProps => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const [editedSection, setEditedSection] = useState(null);

  const isSFCicReasonEnabledFF = useFeatureToggle(FeatureFlag.EnableStraightforwardCicReason);

  const loanFeesView = useSelector(selectFormattedLoanFeesView);
  const isRefinanceFeesFlow = useSelector(selectIsRefinanceFeesFlow);
  const isLoanCicFeeReasonExist = useSelector(selectIsLoanCicFeeReasonExist);
  const taxesInsuranceEscrowed = useSelector(selectTaxesInsuranceEscrowedLoanFeesView);

  const { main, summariesOfTransactions, calculation } = loanFeesView;

  const leSectionOptions = useMemo(
    () => getLeSectionOptions({ taxesInsuranceEscrowed }),
    [taxesInsuranceEscrowed],
  );

  const onEditFeeCic = useCallback(
    () =>
      dispatch(
        openDialogAction({
          header: `${isLoanCicFeeReasonExist ? 'Edit' : 'Create'} FEE CIC`,
          size: ModalSize.XL,
          body: dialogProps => <LoanFeeCicForm isDisableResetStateAction {...dialogProps} />,
        }),
      ),
    [dispatch, isLoanCicFeeReasonExist],
  );

  return (
    <Box sx={wrapperStyles}>
      <Stack sx={wrapperHeaderStyles}>
        <Typography sx={titleTypographyStyles} variant="regular14">
          Fees
        </Typography>
        {!isSFCicReasonEnabledFF && (
          <Button
            data-testid={LoanFeesTestIds.BODY_EDIT_CIC_BUTTON}
            size="small"
            onClick={onEditFeeCic}
          >
            {`${isLoanCicFeeReasonExist ? 'Edit' : 'Create'} FEE CIC`}
          </Button>
        )}
      </Stack>
      {leSectionOptions.map(({ name, description, cdSection, leSection }) => {
        const props = main[cdSection] || {};

        return (
          <LoanFeeSection
            {...props}
            {...loanFeesBodyProps}
            defaultExpanded
            cdSection={cdSection}
            description={description}
            editedSection={editedSection}
            key={cdSection}
            leSection={leSection}
            name={name}
            setEditedSection={setEditedSection}
          />
        );
      })}

      {!_.isEmpty(summariesOfTransactions) && (
        <>
          <Divider sx={dividerStyles} />

          {!isRefinanceFeesFlow && (
            <Typography sx={summariesOfTransactionsStyles} variant="regular14">
              Summaries of Transactions
            </Typography>
          )}
        </>
      )}

      {_.map(summariesOfTransactions, (props, cdSection) => (
        <LoanFeeSection
          {...props}
          {...loanFeesBodyProps}
          defaultExpanded
          isNameAmountSection
          cdSection={cdSection}
          editedSection={editedSection}
          key={cdSection}
          setEditedSection={setEditedSection}
        />
      ))}

      <Stack gap={1.5}>
        <Typography color="text.primary" variant="medium14">
          CALCULATION
        </Typography>

        <LoanFeeSection
          {...loanFeesBodyProps}
          alwaysExpanded
          defaultExpanded
          isNameAmountSection
          cdSection={null}
          editedSection={editedSection}
          isWithHeader={false}
          items={calculation}
          setEditedSection={setEditedSection}
        />
      </Stack>

      <LodeStarQuestions loanId={loanId} />
    </Box>
  );
};

LoanFeesBody.propTypes = {
  ...WithLoadingPropTypes,
};

export default LoanFeesBody;
